import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box, Typography, Grid, IconButton, Button,
  TextField, Dialog, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, CircularProgress,
  Autocomplete, Chip, LinearProgress, Tooltip, Switch,
  useMediaQuery, useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  DeleteOutline as RemoveIcon, Add as AddItemIcon,
  Print as PrintIcon,
  GppGood as SignIcon,
} from '@mui/icons-material';
import axios from '../../config/axios';
import { getApiUrl } from '../../config/environment';
import type { EvolutionAttachedPrintable } from '../evolution/evolutionWorkflowTypes';
import ClinicalHtmlEditor, { clinicalBodyIsEmpty } from './ClinicalHtmlEditor';
import { clinicalHtmlToPlainPreview } from '../../utils/clinicalHtml';
import PrescriptionTemplateChipPicker from './PrescriptionTemplateChipPicker';
import PatientPrescriptionHistoryPicker from './PatientPrescriptionHistoryPicker';
import { usePatientPrescriptionHistory, fetchPrescriptionForModel } from '../../hooks/usePatientPrescriptionHistory';
import { applyPrescriptionModelToForm } from '../../utils/prescriptionModelApply';
import MedicationAutocompleteField, { MedicationSelection } from './MedicationAutocompleteField';
import MedicationPosologyLineField from './MedicationPosologyLineField';
import PrescriptionMedicationDetailsFields from './PrescriptionMedicationDetailsFields';
import {
  PosologyResult,
  PosologySuggestion,
  fetchPosologySuggestion,
} from '../../services/medicationCatalogService';
import {
  PRESCRIPTION_TYPE_OPTIONS,
  normalizePrescriptionType,
  prescriptionTypeLabel,
  resolveEffectivePrescriptionType,
} from '../../utils/prescriptionTypes';
import {
  ROUTE_OPTIONS,
  PosologyLineFields,
  composePosologyLine,
  clearPosologyForNewMedication,
  defaultDosageForForm,
  humanizeFrequency,
  resolvePrescriptionDosage,
} from '../../utils/prescriptionPosologyLine';
import { substanceFromPosologyProfileKey } from '../../utils/prescriptionActiveIngredient';
import PrescriptionEmitOptionsBar from './PrescriptionEmitOptionsBar';
import MemedPrescriptionButton from './MemedPrescriptionButton';
import PrescriptionRenewalAssist from './PrescriptionRenewalAssist';
import { countPrescriptionsToCreate, normalizeScheduledIssueDates } from '../../utils/prescriptionBatchCreate';
import { todayYmdBr } from '../../utils/localCalendarDate';
import {
  clinicFormDialogRootSx,
  clinicLargeFormDialogPaperSx,
  MOBILE_FORM_SURFACE_CLASS,
  mobileDialogFormContentSx,
  mobileFormFieldLabelProps,
  mobileFullScreenDialogPaperSx,
} from '../../utils/mobileFormLayout';
import { submitPrescriptionForms } from '../../utils/prescriptionSubmit';
import {
  buildMultiMonthPrintSettings,
  getMultiMonthCount,
  getMultiMonthStep,
} from '../../utils/prescriptionMultiMonth';
import {
  getHideIssueDateOnPrint,
  setHideIssueDateOnPrint,
} from '../../utils/prescriptionPrintSettings';
import { prescriptionHasMultipleVias } from '../../utils/prescriptionPrintDocument';
import {
  prescriptionItemPackageFields,
  resolvePrescriptionPackageCount,
} from '../../utils/prescriptionPackageCount';

const routeOptions = ROUTE_OPTIONS;
const formOptions = ['Comprimido', 'Cápsula', 'Drágea', 'Solução Oral', 'Suspensão', 'Xarope', 'Gotas', 'Pomada', 'Creme', 'Gel', 'Solução Injetável', 'Supositório', 'Aerossol', 'Spray', 'Colírio', 'Sachê', 'Pó'];

const emptyItem = {
  medication_name: '', active_ingredient: '', concentration: '', pharmaceutical_form: '',
  dosage: '', frequency: '', route: 'Oral', duration: '', quantity: '',
  instructions: '', special_instructions: '', is_controlled: false, control_class: '',
  is_antimicrobial: false, sncr_receita_tipo: '' as string | null,
  is_continuous_use: false, is_two_packages: false, package_count: 1, sort_order: 0,
};

const sectionLabelSx = {
  fontSize: '0.68rem',
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase' as const,
  color: '#64748B',
};

const ITEM_FLAGS: Array<{ field: string; label: string; color: string; bg: string }> = [
  { field: 'is_controlled', label: 'Controlado', color: '#B91C1C', bg: '#FEF2F2' },
  { field: 'is_antimicrobial', label: 'Antimicrobiano', color: '#B45309', bg: '#FFFBEB' },
  { field: 'is_continuous_use', label: 'Uso contínuo', color: '#0F766E', bg: '#F0FDFA' },
];

const PACKAGE_CHIP = { color: '#1D4ED8', bg: '#EFF6FF' };

const PrescriptionPackageCountChip: React.FC<{
  count: number;
  onChange: (count: number) => void;
}> = ({ count, onChange }) => {
  const active = count > 1;
  const label = active ? `${count} embalagens` : '2 embalagens';
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
      <Chip
        size="small"
        label={label}
        onClick={() => onChange(active ? 1 : 2)}
        aria-pressed={active}
        sx={{
          height: 24,
          fontSize: '0.72rem',
          fontWeight: 600,
          borderRadius: 1.5,
          border: '1px solid',
          borderColor: active ? PACKAGE_CHIP.color : '#E2E8F0',
          bgcolor: active ? PACKAGE_CHIP.bg : '#FFFFFF',
          color: active ? PACKAGE_CHIP.color : '#64748B',
          transition: 'all 120ms ease',
          '&:hover': { bgcolor: active ? PACKAGE_CHIP.bg : '#F8FAFC', borderColor: active ? PACKAGE_CHIP.color : '#CBD5E1' },
        }}
      />
      {active && (
        <Tooltip title="Mais uma embalagem">
          <IconButton
            size="small"
            aria-label="Aumentar número de embalagens"
            onClick={() => onChange(Math.min(99, count + 1))}
            sx={{
              width: 24,
              height: 24,
              color: PACKAGE_CHIP.color,
              border: `1px solid ${PACKAGE_CHIP.color}`,
              bgcolor: PACKAGE_CHIP.bg,
              '&:hover': { bgcolor: '#DBEAFE' },
            }}
          >
            <AddItemIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};

const ItemFlagChip: React.FC<{
  label: string;
  active: boolean;
  color: string;
  bg: string;
  onToggle: () => void;
}> = ({ label, active, color, bg, onToggle }) => (
  <Chip
    size="small"
    label={label}
    onClick={onToggle}
    aria-pressed={active}
    sx={{
      height: 24,
      fontSize: '0.72rem',
      fontWeight: 600,
      borderRadius: 1.5,
      border: '1px solid',
      borderColor: active ? color : '#E2E8F0',
      bgcolor: active ? bg : '#FFFFFF',
      color: active ? color : '#64748B',
      transition: 'all 120ms ease',
      '&:hover': { bgcolor: active ? bg : '#F8FAFC', borderColor: active ? color : '#CBD5E1' },
    }}
  />
);

/** Descarta o estado só de tela (`_rowKey`, `_posologyRev`) antes de salvar. */
function stripUiFields(item: any): any {
  const base = Object.fromEntries(Object.entries(item).filter(([key]) => !key.startsWith('_')));
  const count = resolvePrescriptionPackageCount(base);
  return { ...base, ...prescriptionItemPackageFields(count) };
}

function escapeHtmlText(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function structuredItemsToFreeTextHtml(items: any[]): string {
  return items
    .filter((i) => i?.medication_name?.trim())
    .map((i, idx) => {
      const name = [i.medication_name, i.concentration].filter((v: string) => v?.trim()).join(' ');
      const posology = composePosologyLine(i);
      const lines = [`<strong>${idx + 1}. ${escapeHtmlText(name)}</strong>`];
      if (posology) lines.push(escapeHtmlText(posology));
      if (i.instructions?.trim()) lines.push(escapeHtmlText(i.instructions.trim()));
      return `<p>${lines.join('<br>')}</p>`;
    })
    .join('');
}

function summaryFromPrescription(pr: any, fallbackItems: any[], bodyText?: string): string {
  const lines: string[] = [];
  if (pr?.prescription_number) lines.push(`Receituário nº ${pr.prescription_number}`);
  if ((pr?.format || (bodyText ? 'free_text' : 'structured')) === 'free_text') {
    const body = clinicalHtmlToPlainPreview(pr?.body_text || bodyText || '', 200);
    if (body) lines.push(body);
  } else {
    const items = pr?.items?.length ? pr.items : fallbackItems;
    items.filter((i: any) => i?.medication_name?.trim()).forEach((i: any, idx: number) => {
      const posology = composePosologyLine(i);
      lines.push(`${idx + 1}. ${i.medication_name}${posology ? ` — ${posology}` : ''}`);
    });
  }
  if (pr?.notes) lines.push(`Obs.: ${pr.notes}`);
  return lines.join('\n') || 'Receituário registrado.';
}

export type PrescriptionFormCompletion = 'save' | 'print' | 'print_first_via' | 'sign';

export type PrescriptionFormSuccessResult = {
  printables: EvolutionAttachedPrintable[];
  completion: 'save' | 'print' | 'sign';
  savedPrescriptions: any[];
  /** Quando completion é print: imprime só a 1ª via de cada bloco (controlado/antimicrobiano). */
  printFirstViaOnly?: boolean;
};

export interface PrescriptionFormDialogProps {
  open: boolean;
  onClose: () => void;
  patientId: string;
  professionalId: string;
  initialPatient?: any;
  initialProfessional?: any;
  /** Template receituário (API) aplicado ao abrir */
  prefillTemplate?: any | null;
  /** Edição de receituário já vinculado à evolução */
  existingPrescriptionId?: string | null;
  existingClientKey?: string | null;
  /** Ex.: abrir gerenciador de modelos na página de Receituários. */
  onManageTemplates?: () => void;
  onSuccess: (result: PrescriptionFormSuccessResult) => void | Promise<void>;
  /** Chamado quando uma receita Memed é salva (em outra aba) a partir deste formulário. */
  onMemedSaved?: () => void;
}

const PrescriptionFormDialog: React.FC<PrescriptionFormDialogProps> = ({
  open,
  onClose,
  patientId,
  professionalId,
  initialPatient,
  initialProfessional,
  prefillTemplate,
  existingPrescriptionId = null,
  existingClientKey = null,
  onManageTemplates,
  onSuccess,
  onMemedSaved,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [savingAction, setSavingAction] = useState<PrescriptionFormCompletion | null>(null);
  const [templates, setTemplates] = useState<any[]>([]);
  const [patientSearch, setPatientSearch] = useState('');
  const [patientOptions, setPatientOptions] = useState<any[]>([]);
  const [selectedPatient, setSelectedPatient] = useState<any>(null);
  const [professionalSearch, setProfessionalSearch] = useState('');
  const [professionalOptions, setProfessionalOptions] = useState<any[]>([]);
  const [selectedProfessional, setSelectedProfessional] = useState<any>(null);
  /** Sugestão de posologia por linha de medicamento (índice do item). */
  const [posologyHints, setPosologyHints] = useState<Record<number, PosologyResult | null>>({});
  const [posologyLoading, setPosologyLoading] = useState<Record<number, boolean>>({});
  const [items, setItems] = useState<any[]>(() => [{ ...emptyItem, _rowKey: 'rx-row-0' }]);
  const [format, setFormat] = useState<'structured' | 'free_text'>('structured');
  const [bodyText, setBodyText] = useState('');
  const [prescriptionForm, setPrescriptionForm] = useState({
    prescription_type: 'simple',
    diagnosis: '', diagnosis_code: '', clinical_indication: '',
    notes: '', internal_notes: '', renewal_date: '', issue_date: '',
  });
  const [loadingExisting, setLoadingExisting] = useState(false);
  const [splitMedications, setSplitMedications] = useState(false);
  const [scheduleSpecialItemsOnly, setScheduleSpecialItemsOnly] = useState(false);
  const [scheduledIssueDates, setScheduledIssueDates] = useState<string[]>(() => [todayYmdBr()]);
  const [printSettings, setPrintSettings] = useState<Record<string, unknown>>({});
  const [renewalReturnMonths, setRenewalReturnMonths] = useState(6);
  const [renewalTabletsPerMonth, setRenewalTabletsPerMonth] = useState(30);
  const effectiveFormat = format;
  const multiMonthCount = getMultiMonthCount(printSettings);
  const multiMonthStep = getMultiMonthStep(printSettings);
  const hideIssueDateOnPrint = getHideIssueDateOnPrint(printSettings);
  /** Evita resetar o formulário quando initialPatient/initialProfessional mudam após abrir o diálogo. */
  const openSessionRef = useRef(false);
  const loadSessionRef = useRef<string | null>(null);
  /** Data de emissão original ao abrir edição (qual job faz PUT). */
  const editingOriginalIssueDateRef = useRef<string | null>(null);
  const rowKeyRef = useRef(0);

  const createEmptyItem = () => ({
    ...emptyItem,
    _rowKey: `rx-row-${rowKeyRef.current++}`,
  });

  const withRowKey = (item: any, idx: number) => {
    const packageCount = resolvePrescriptionPackageCount(item);
    return {
      ...emptyItem,
      ...item,
      ...prescriptionItemPackageFields(packageCount),
      id: undefined,
      sort_order: idx,
      _rowKey: item?.id ? `rx-loaded-${item.id}` : `rx-row-${rowKeyRef.current++}`,
    };
  };

  const activePatientId = selectedPatient?.id || patientId || null;
  const { history: patientPrescriptionHistory, loading: loadingPatientPrescriptionHistory } =
    usePatientPrescriptionHistory(activePatientId, {
      enabled: open && Boolean(activePatientId),
      excludePrescriptionId: existingPrescriptionId,
    });

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(getApiUrl('/clinic/prescriptions/templates'), { params: { is_active: true } });
        setTemplates(res.data?.data || []);
      } catch {
        setTemplates([]);
      }
    }, 0);
    return () => clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (!open) {
      openSessionRef.current = false;
      loadSessionRef.current = null;
      return;
    }

    let cancelled = false;

    const ymdFromApi = (value?: string | null) => {
      if (!value) return '';
      return String(value).split('T')[0] || '';
    };

    const bootstrapNew = () => {
      setSelectedPatient(initialPatient?.id ? initialPatient : (patientId ? { id: patientId, full_name: '…' } : null));
      setSelectedProfessional(initialProfessional?.id ? initialProfessional : (professionalId ? { id: professionalId, full_name: '…' } : null));
      setPatientOptions(initialPatient ? [initialPatient] : []);
      setProfessionalOptions(initialProfessional ? [initialProfessional] : []);
      setPatientSearch('');
      setProfessionalSearch('');
      setPrescriptionForm({
        prescription_type: 'simple', diagnosis: '', diagnosis_code: '', clinical_indication: '',
        notes: '', internal_notes: '', renewal_date: '', issue_date: '',
      });
      setItems([createEmptyItem()]);
      setFormat('structured');
      setBodyText('');
      setPosologyHints({});
      setSplitMedications(false);
      setScheduleSpecialItemsOnly(false);
      setScheduledIssueDates([todayYmdBr()]);
      setPrintSettings({});
      setRenewalReturnMonths(6);
      setRenewalTabletsPerMonth(30);
      if (prefillTemplate) {
        setPrescriptionForm((prev: any) => ({
          ...prev,
          prescription_type: normalizePrescriptionType(prefillTemplate.prescription_type),
          diagnosis: prefillTemplate.default_diagnosis || '',
          diagnosis_code: prefillTemplate.default_diagnosis_code || '',
          notes: prefillTemplate.default_notes || '',
        }));
        const tplFormat = prefillTemplate.format === 'free_text' ? 'free_text' : 'structured';
        setFormat(tplFormat);
        setBodyText(tplFormat === 'free_text' ? (prefillTemplate.body_text || '') : '');
        const tplItems = (prefillTemplate.items || []).map((it: any, idx: number) => withRowKey(it, idx));
        setItems(tplItems.length > 0 ? tplItems : [createEmptyItem()]);
      }
    };

    const bootstrap = async () => {
      if (existingPrescriptionId) {
        if (loadSessionRef.current === existingPrescriptionId) return;
        setLoadingExisting(true);
        try {
          const { data } = await axios.get(getApiUrl(`/clinic/prescriptions/${existingPrescriptionId}`));
          if (cancelled) return;
          const pr = data?.data ?? data;
          loadSessionRef.current = existingPrescriptionId;
          openSessionRef.current = true;
          const issueYmd = ymdFromApi(pr?.issue_date) || todayYmdBr();
          editingOriginalIssueDateRef.current = issueYmd;
          setSelectedPatient(
            pr?.patient
            ?? (initialPatient?.id ? initialPatient : { id: patientId, full_name: '…' }),
          );
          setSelectedProfessional(
            pr?.professional
            ?? (initialProfessional?.id ? initialProfessional : { id: professionalId, full_name: '…' }),
          );
          setPatientOptions(pr?.patient ? [pr.patient] : initialPatient ? [initialPatient] : []);
          setProfessionalOptions(pr?.professional ? [pr.professional] : initialProfessional ? [initialProfessional] : []);
          setPrescriptionForm({
            prescription_type: normalizePrescriptionType(pr?.prescription_type),
            diagnosis: pr?.diagnosis || '',
            diagnosis_code: pr?.diagnosis_code || '',
            clinical_indication: pr?.clinical_indication || '',
            notes: pr?.notes || '',
            internal_notes: pr?.internal_notes || '',
            renewal_date: ymdFromApi(pr?.renewal_date),
            issue_date: issueYmd,
          });
          const prFormat = pr?.format === 'free_text' ? 'free_text' : 'structured';
          setFormat(prFormat);
          setBodyText(prFormat === 'free_text' ? (pr?.body_text || '') : '');
          const loadedItems = (pr?.items || []).map((it: any, idx: number) => withRowKey(it, idx));
          setItems(loadedItems.length > 0 ? loadedItems : [createEmptyItem()]);
          setPosologyHints({});
          setSplitMedications(false);
          setScheduleSpecialItemsOnly(false);
          setScheduledIssueDates([issueYmd]);
          const ps = pr?.print_settings && typeof pr.print_settings === 'object' ? pr.print_settings : {};
          setPrintSettings({ ...(ps as Record<string, unknown>) });
        } catch (err) {
          console.error(err);
        } finally {
          if (!cancelled) setLoadingExisting(false);
        }
        return;
      }

      editingOriginalIssueDateRef.current = null;
      if (loadSessionRef.current) {
        loadSessionRef.current = null;
        openSessionRef.current = false;
      }
      if (openSessionRef.current) return;
      openSessionRef.current = true;
      bootstrapNew();
    };

    void bootstrap();
    return () => { cancelled = true; };
  }, [open, patientId, professionalId, initialPatient, initialProfessional, prefillTemplate, existingPrescriptionId]);

  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(getApiUrl('/clinic/prescriptions-search/patients'), { params: { search: patientSearch || '' } });
        setPatientOptions(res.data?.data || []);
      } catch { /* ignore */ }
    }, patientSearch.length > 0 ? 300 : 50);
    return () => clearTimeout(timer);
  }, [patientSearch, open]);

  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(getApiUrl('/clinic/prescriptions-search/professionals'), { params: { search: professionalSearch || '' } });
        setProfessionalOptions(res.data?.data || []);
      } catch { /* ignore */ }
    }, professionalSearch.length > 0 ? 300 : 50);
    return () => clearTimeout(timer);
  }, [professionalSearch, open]);

  useEffect(() => {
    if (!open || !initialPatient?.id) return;
    setSelectedPatient(initialPatient);
    setPatientOptions((o) => (o.some((p: any) => p.id === initialPatient.id) ? o : [initialPatient, ...o]));
  }, [open, initialPatient]);

  useEffect(() => {
    if (!open || !initialProfessional?.id) return;
    setSelectedProfessional(initialProfessional);
    setProfessionalOptions((o) => (o.some((p: any) => p.id === initialProfessional.id) ? o : [initialProfessional, ...o]));
  }, [open, initialProfessional]);

  useEffect(() => {
    if (!open || effectiveFormat !== 'structured') return;
    const resolved = normalizePrescriptionType(
      resolveEffectivePrescriptionType(prescriptionForm.prescription_type, items),
    );
    if (resolved !== prescriptionForm.prescription_type) {
      setPrescriptionForm((prev) => ({ ...prev, prescription_type: resolved }));
    }
  }, [items, open, effectiveFormat, prescriptionForm.prescription_type]);

  const resetAndClose = () => {
    onClose();
  };

  const handleReorderTemplates = async (orderedIds: string[]) => {
    try {
      await axios.put(getApiUrl('/clinic/prescriptions/templates/reorder'), { ids: orderedIds });
      setTemplates((prev) => {
        const byId = new Map(prev.map((t) => [String(t.id), t]));
        return orderedIds.map((id, index) => {
          const row = byId.get(String(id));
          return row ? { ...row, sort_order: index } : null;
        }).filter(Boolean) as any[];
      });
    } catch {
      try {
        const res = await axios.get(getApiUrl('/clinic/prescriptions/templates'), { params: { is_active: true } });
        setTemplates(res.data?.data || []);
      } catch { /* ignore */ }
    }
  };

  const setItemsWithRowKeys = (next: any[] | ((prev: any[]) => any[])) => {
    setItems((prev) => {
      const rows = typeof next === 'function' ? next(prev) : next;
      if (!Array.isArray(rows) || rows.length === 0) return [createEmptyItem()];
      return rows.map((item, idx) => (item._rowKey ? item : withRowKey(item, idx)));
    });
    setPosologyHints({});
  };

  const applyTemplate = async (template: any) => {
    applyPrescriptionModelToForm(template, {
      setPrescriptionForm,
      setFormat,
      setBodyText,
      setItems: setItemsWithRowKeys,
    }, emptyItem, { items, format, bodyText });
  };

  const applyPatientPrescription = async (prescription: any) => {
    const source = await fetchPrescriptionForModel(prescription);
    applyPrescriptionModelToForm(source, {
      setPrescriptionForm,
      setFormat,
      setBodyText,
      setItems: setItemsWithRowKeys,
    }, emptyItem, { items, format, bodyText });

    const currentProfessional = initialProfessional?.id
      ? initialProfessional
      : (professionalId ? { id: professionalId, full_name: selectedProfessional?.full_name || '…' } : null);
    if (currentProfessional?.id) {
      setSelectedProfessional(currentProfessional);
      setProfessionalOptions((options) =>
        options.some((row) => row.id === currentProfessional.id)
          ? options
          : [currentProfessional, ...options],
      );
    }
  };

  const addItem = () => {
    setItems((current) => [createEmptyItem(), ...current]);
    setPosologyHints((hints) => {
      const next: Record<number, PosologyResult | null> = {};
      Object.entries(hints).forEach(([key, value]) => {
        next[Number(key) + 1] = value;
      });
      return next;
    });
  };
  const removeItem = (idx: number) => {
    if (items.length <= 1) return;
    setItems((current) => current.filter((_, i) => i !== idx));
    setPosologyHints((hints) => {
      const next: Record<number, PosologyResult | null> = {};
      Object.entries(hints).forEach(([key, value]) => {
        const i = Number(key);
        if (i < idx) next[i] = value;
        else if (i > idx) next[i - 1] = value;
      });
      return next;
    });
  };
  const updateItem = (idx: number, field: string, value: any) => {
    const updated = [...items];
    updated[idx] = { ...updated[idx], [field]: value };
    setItems(updated);
  };

  /**
   * Grava a frase de posologia nas colunas estruturadas. Não mexe em
   * `_posologyRev`: o campo não pode ser reescrito enquanto o médico digita.
   */
  const updatePosologyLine = (idx: number, fields: PosologyLineFields) => {
    setItems((current) => {
      const updated = [...current];
      updated[idx] = { ...updated[idx], ...fields };
      return updated;
    });
  };

  /** Preenche só o que estiver vazio: nada que o médico digitou é sobrescrito. */
  const applyPosology = (idx: number, suggestion: PosologySuggestion) => {
    setItems((current) => {
      const updated = [...current];
      const item = { ...updated[idx] };
      const route =
        suggestion.route && routeOptions.includes(suggestion.route) ? suggestion.route : item.route;

      if (!item.dosage && suggestion.dosage) {
        item.dosage = resolvePrescriptionDosage({
          dosage: suggestion.dosage,
          pharmaceutical_form: item.pharmaceutical_form,
          route,
          concentration: item.concentration,
        });
      }
      if (!item.frequency && suggestion.frequency) item.frequency = humanizeFrequency(suggestion.frequency);
      if (!item.duration && suggestion.duration) item.duration = suggestion.duration;
      item.route = route;
      if (!item.quantity && suggestion.quantity) item.quantity = suggestion.quantity;
      if (suggestion.is_controlled != null) item.is_controlled = suggestion.is_controlled;
      if (suggestion.control_class) item.control_class = suggestion.control_class;
      if (suggestion.is_antimicrobial != null) item.is_antimicrobial = suggestion.is_antimicrobial;
      if (suggestion.sncr_receita_tipo) item.sncr_receita_tipo = suggestion.sncr_receita_tipo;
      item._posologyRev = (item._posologyRev || 0) + 1;

      updated[idx] = item;
      return updated;
    });
  };

  /**
   * Sem posologia verificada, ainda assim adianta a unidade de administração
   * da forma farmacêutica. Frequência e duração seguem em branco — são
   * decisão clínica.
   */
  const applyFormDefaults = (idx: number) => {
    setItems((current) => {
      const updated = [...current];
      const item = { ...updated[idx] };
      if (item.dosage) return current;
      const suggested = defaultDosageForForm(item.pharmaceutical_form, item.route);
      if (!suggested) return current;
      item.dosage = suggested;
      item._posologyRev = (item._posologyRev || 0) + 1;
      updated[idx] = item;
      return updated;
    });
  };

  /**
   * Aplica a apresentação do catálogo e busca a posologia. Bula verificada e
   * histórico entram direto na linha; sugestão de IA aguarda "Aplicar".
   */
  const applyMedicationSelection = async (idx: number, selection: MedicationSelection) => {
    const activeIngredient =
      selection.active_ingredient
      || substanceFromPosologyProfileKey(selection.posology_profile_key);

    setItems((current) => {
      const updated = [...current];
      let item = clearPosologyForNewMedication({
        ...updated[idx],
        medication_name: selection.medication_name,
      });

      if (activeIngredient) item.active_ingredient = activeIngredient;
      if (selection.concentration) item.concentration = selection.concentration;
      if (selection.pharmaceutical_form) item.pharmaceutical_form = selection.pharmaceutical_form;
      if (selection.quantity) item.quantity = selection.quantity;
      if (selection.route) item.route = selection.route;
      if (selection.control_class) item.control_class = selection.control_class;
      item.is_controlled = Boolean(selection.is_controlled);
      item.is_antimicrobial = Boolean(selection.is_antimicrobial);
      if (selection.sncr_receita_tipo) item.sncr_receita_tipo = selection.sncr_receita_tipo;

      updated[idx] = item;
      return updated;
    });

    setPosologyHints((current) => ({ ...current, [idx]: null }));
    setPosologyLoading((current) => ({ ...current, [idx]: true }));

    try {
      const result = await fetchPosologySuggestion({
        medication_name: selection.medication_name,
        active_ingredient: activeIngredient ?? null,
        concentration: selection.concentration ?? null,
        pharmaceutical_form: selection.pharmaceutical_form ?? null,
        professional_id: selectedProfessional?.id || null,
        posology_profile_key: selection.posology_profile_key ?? null,
        catalog_id: selection.catalog_id ?? null,
      });

      const top = result.suggestions[0];
      const autoApply =
        Boolean(top)
        && (result.source === 'verified_bula'
          || result.source === 'cmed_draft'
          || result.source === 'professional_history'
          || result.source === 'unit_history');

      if (autoApply) applyPosology(idx, top!);
      else applyFormDefaults(idx);

      setPosologyHints((current) => ({ ...current, [idx]: result }));
    } catch {
      applyFormDefaults(idx);
      setPosologyHints((current) => ({ ...current, [idx]: null }));
    } finally {
      setPosologyLoading((current) => ({ ...current, [idx]: false }));
    }
  };

  const medicationCount = items.filter((i) => i.medication_name?.trim()).length;

  const handleFormatToggle = (freeText: boolean) => {
    if (freeText && clinicalBodyIsEmpty(bodyText) && medicationCount > 0) {
      setBodyText(structuredItemsToFreeTextHtml(items));
    }
    setFormat(freeText ? 'free_text' : 'structured');
  };
  const printHasMultipleVias = useMemo(
    () => prescriptionHasMultipleVias({
      origin: 'local',
      prescription_type: prescriptionForm.prescription_type,
      items,
      format: effectiveFormat,
    }),
    [prescriptionForm.prescription_type, items, effectiveFormat],
  );
  const namedItemsForBatch = useMemo(
    () => items.filter((i) => String(i.medication_name || '').trim()),
    [items],
  );
  const prescriptionsToCreate = countPrescriptionsToCreate({
    format: effectiveFormat,
    medicationCount,
    splitMedications,
    scheduledDates: scheduledIssueDates,
    items: namedItemsForBatch,
    scheduleSpecialItemsOnly,
  });

  const handleSave = async (completion: PrescriptionFormCompletion) => {
    if (!selectedPatient?.id) return;
    if (effectiveFormat === 'structured' && !items.some((i) => i.medication_name.trim())) return;
    if (effectiveFormat === 'free_text' && clinicalBodyIsEmpty(bodyText)) return;

    const printFirstViaOnly = completion === 'print_first_via';
    const resultCompletion: PrescriptionFormSuccessResult['completion'] =
      printFirstViaOnly || completion === 'print' ? 'print' : completion;

    setSavingAction(completion);
    try {
      const savedList = await submitPrescriptionForms({
        editingId: existingPrescriptionId,
        editingOriginalIssueDate: editingOriginalIssueDateRef.current,
        prescriptionForm,
        format: effectiveFormat,
        bodyText,
        items,
        splitMedications,
        scheduledDates: scheduledIssueDates,
        scheduleSpecialItemsOnly,
        patientId: selectedPatient.id,
        professionalId: selectedProfessional?.id || null,
        printSettings: buildMultiMonthPrintSettings(printSettings, multiMonthCount),
        stripUiFields,
      });

      const printables: EvolutionAttachedPrintable[] = [];
      for (let i = 0; i < savedList.length; i++) {
        const saved = savedList[i];
        const newId = saved?.id || existingPrescriptionId;
        const savedItems = saved?.items?.length ? saved.items : items;
        const subtype = prescriptionTypeLabel(String(saved?.prescription_type ?? prescriptionForm.prescription_type));
        const firstMed = effectiveFormat === 'free_text'
          ? ''
          : (savedItems.find((it: any) => it.medication_name?.trim())?.medication_name || '');
        const dateSuffix = saved?.issue_date && savedList.length > 1
          ? ` (${new Date(`${saved.issue_date}T12:00:00`).toLocaleDateString('pt-BR')})`
          : '';
        const tail = firstMed ? `${subtype} — ${firstMed}${dateSuffix}` : `${subtype}${dateSuffix}`;
        const templateName = /^receitu[aá]?rio(\s|$|[—\-–])/i.test(tail)
          ? tail
          : `Receituário — ${tail}`;
        printables.push({
          clientKey: existingPrescriptionId && existingClientKey && i === 0
            ? existingClientKey
            : crypto.randomUUID(),
          category: 'prescription',
          templateId: prefillTemplate?.id,
          templateName,
          bodyText: summaryFromPrescription(saved, savedItems, bodyText),
          linkedDocumentId: newId,
          linkedDocumentType: 'prescription',
        });
      }
      void Promise.resolve(onSuccess({
        printables,
        completion: resultCompletion,
        savedPrescriptions: savedList,
        printFirstViaOnly,
      })).catch((err) => {
        console.error(err);
      });
      resetAndClose();
    } catch (err: any) {
      console.error(err);
    } finally {
      setSavingAction(null);
    }
  };

  const showReuse = templates.length > 0 || Boolean(activePatientId);
  const showRenewalAssist = true;
  const showPrintMonthSettings = effectiveFormat === 'structured';
  const saving = savingAction !== null;
  const canSave = !saving && Boolean(selectedPatient)
    && (effectiveFormat === 'free_text' ? !clinicalBodyIsEmpty(bodyText) : medicationCount > 0);
  const saveOnlyLabel = prescriptionsToCreate > 1 ? `Salvar (${prescriptionsToCreate})` : 'Salvar';
  return (
    <Dialog
      open={open}
      onClose={resetAndClose}
      maxWidth={false}
      fullWidth
      fullScreen={fullScreen}
      scroll="paper"
      PaperProps={{
        className: fullScreen ? MOBILE_FORM_SURFACE_CLASS : undefined,
        sx: {
          ...(fullScreen ? mobileFullScreenDialogPaperSx : clinicLargeFormDialogPaperSx(false)),
          borderTop: '3px solid #0D9488',
        },
      }}
      sx={fullScreen ? undefined : clinicFormDialogRootSx}
    >
      {loadingExisting && <LinearProgress sx={{ height: 2 }} />}

      <Box
        className={fullScreen ? MOBILE_FORM_SURFACE_CLASS : undefined}
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 0.5,
          flexShrink: 0,
          overflow: 'visible',
          px: { xs: 2, md: 3 },
          pt: { xs: fullScreen ? 1.25 : 2, md: 2.25 },
          pb: fullScreen ? 2 : 1.5,
          bgcolor: '#F8FAFC',
          borderBottom: '1px solid #E2E8F0',
        }}
      >
        <Grid container spacing={fullScreen ? 2 : 1.5} sx={{ flex: 1, minWidth: 0, overflow: 'visible' }}>
          {existingPrescriptionId && (
            <Grid item xs={12}>
              <Typography sx={{ fontSize: '0.95rem', fontWeight: 800, color: '#0F766E', letterSpacing: '-0.01em' }}>
                Editar receituário
              </Typography>
            </Grid>
          )}
          <Grid item xs={12} md={5} sx={{ overflow: 'visible' }}>
            <Autocomplete
              options={patientOptions}
              getOptionLabel={(o: any) => `${o.full_name}${o.cpf ? ` - CPF: ${o.cpf}` : ''}`}
              value={selectedPatient}
              onChange={(_, v) => setSelectedPatient(v)}
              onInputChange={(_, v) => setPatientSearch(v)}
              filterOptions={(x) => x}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Paciente *"
                  fullWidth
                  size="small"
                  sx={{ bgcolor: '#FFFFFF' }}
                  InputLabelProps={{ ...params.InputLabelProps, ...mobileFormFieldLabelProps(fullScreen) }}
                />
              )}
              noOptionsText="Nenhum paciente encontrado"
            />
          </Grid>
          <Grid item xs={12} sm={7} md={4} sx={{ overflow: 'visible' }}>
            <Autocomplete
              options={professionalOptions}
              getOptionLabel={(o: any) => `${o.full_name}${o.registration_number ? ` — ${o.registration_type || 'CRM'} ${o.registration_number}` : ''}`}
              value={selectedProfessional}
              onChange={(_, v) => setSelectedProfessional(v)}
              onInputChange={(_, v) => setProfessionalSearch(v)}
              filterOptions={(x) => x}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Profissional"
                  fullWidth
                  size="small"
                  sx={{ bgcolor: '#FFFFFF' }}
                  InputLabelProps={{ ...params.InputLabelProps, ...mobileFormFieldLabelProps(fullScreen) }}
                />
              )}
              noOptionsText="Nenhum profissional encontrado"
            />
          </Grid>
          <Grid item xs={12} sm={5} md={3} sx={{ overflow: 'visible' }}>
            <FormControl fullWidth size="small" sx={{ bgcolor: '#FFFFFF', overflow: 'visible' }}>
              <InputLabel shrink={fullScreen ? true : undefined}>Tipo de receituário</InputLabel>
              <Select
                value={prescriptionForm.prescription_type}
                label="Tipo de receituário"
                onChange={(e) => setPrescriptionForm({ ...prescriptionForm, prescription_type: e.target.value })}
              >
                {PRESCRIPTION_TYPE_OPTIONS.map((opt) => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        <IconButton
          onClick={resetAndClose}
          size="small"
          aria-label="Fechar"
          sx={{ color: '#64748B', flexShrink: 0, mt: 0.25 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent
        className={fullScreen ? MOBILE_FORM_SURFACE_CLASS : undefined}
        sx={{
          p: 0,
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          overflow: { xs: 'auto', md: 'hidden' },
          ...mobileDialogFormContentSx(fullScreen),
        }}
      >
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            overflowY: { md: 'auto' },
            overflowX: 'visible',
            px: { xs: 2, md: 3 },
            py: 2.5,
            display: 'flex',
            flexDirection: 'column',
            gap: 2.5,
          }}
        >
          <PrescriptionEmitOptionsBar
            format={effectiveFormat}
            medicationCount={medicationCount}
            splitMedications={splitMedications}
            onSplitMedicationsChange={setSplitMedications}
            scheduledDates={scheduledIssueDates}
            onScheduledDatesChange={setScheduledIssueDates}
            items={items}
            scheduleSpecialItemsOnly={scheduleSpecialItemsOnly}
            onScheduleSpecialItemsOnlyChange={setScheduleSpecialItemsOnly}
            hideIssueDateOnPrint={hideIssueDateOnPrint}
            onHideIssueDateOnPrintChange={(v) => setPrintSettings(setHideIssueDateOnPrint(printSettings, v))}
          />

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
              px: 1.5,
              py: 0.75,
              borderRadius: 1.5,
              border: '1px solid',
              borderColor: effectiveFormat === 'free_text' ? '#99F6E4' : '#E2E8F0',
              bgcolor: effectiveFormat === 'free_text' ? '#F0FDFA' : '#FFFFFF',
            }}
          >
            <Switch
              size="small"
              checked={effectiveFormat === 'free_text'}
              onChange={(e) => handleFormatToggle(e.target.checked)}
              inputProps={{ 'aria-label': 'Receituário em texto livre' }}
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#0F172A', lineHeight: 1.3 }}>
                Receituário em texto livre
              </Typography>
              <Typography sx={{ fontSize: '0.72rem', color: '#64748B', lineHeight: 1.35 }}>
                {effectiveFormat === 'free_text'
                  ? 'Digite a receita no campo abaixo, sem a base de medicamentos e a posologia estruturada.'
                  : 'Ative para digitar a receita livremente em vez de usar a base de medicamentos.'}
              </Typography>
            </Box>
          </Box>

          {effectiveFormat === 'free_text' ? (
            <ClinicalHtmlEditor
              label="Texto do receituário"
              minRows={14}
              placeholder="Digite o conteúdo do receituário. O timbrado (clínica/profissional/paciente), a data e o rodapé entram automaticamente na impressão."
              value={bodyText}
              onChange={setBodyText}
            />
          ) : (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
                <Typography sx={sectionLabelSx}>Medicamentos</Typography>
                {medicationCount > 0 && (
                  <Box
                    component="span"
                    sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#0F766E', bgcolor: '#F0FDFA', px: 0.75, borderRadius: 1 }}
                  >
                    {medicationCount}
                  </Box>
                )}
                <Box sx={{ flex: 1 }} />
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AddItemIcon fontSize="small" />}
                  onClick={addItem}
                  sx={{
                    textTransform: 'none', fontWeight: 600, fontSize: '0.78rem',
                    borderColor: '#CBD5E1', color: '#0F172A', borderRadius: 1.5, py: 0.25,
                    '&:hover': { borderColor: '#2563EB', color: '#2563EB', bgcolor: '#EFF6FF' },
                  }}
                >
                  Adicionar medicamento
                </Button>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {items.map((item, index) => (
                  <Box
                    key={item._rowKey ?? index}
                    sx={{
                      display: 'flex',
                      gap: 1.5,
                      p: 1.5,
                      pl: 1.25,
                      pt: fullScreen ? 1.75 : 1.5,
                      overflow: 'visible',
                      bgcolor: '#FFFFFF',
                      border: '1px solid #E2E8F0',
                      borderRadius: 2,
                      transition: 'border-color 140ms ease, box-shadow 140ms ease',
                      '&:hover': { borderColor: '#CBD5E1' },
                      '&:focus-within': {
                        borderColor: '#0D9488',
                        boxShadow: '0 0 0 3px rgba(13,148,136,0.08)',
                      },
                      '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                    }}
                  >
                    <Box
                      sx={{
                        width: 24, height: 24, mt: 0.75, flexShrink: 0,
                        borderRadius: '50%', display: 'grid', placeItems: 'center',
                        bgcolor: item.medication_name?.trim() ? '#0D9488' : '#F1F5F9',
                        color: item.medication_name?.trim() ? '#FFFFFF' : '#64748B',
                        fontSize: '0.72rem', fontWeight: 700,
                      }}
                    >
                      {index + 1}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <MedicationAutocompleteField
                        value={item.medication_name}
                        onChange={(v) => updateItem(index, 'medication_name', v)}
                        onMedicationSelected={(selection) => { void applyMedicationSelection(index, selection); }}
                        inputLabelProps={mobileFormFieldLabelProps(fullScreen)}
                      />
                      <MedicationPosologyLineField
                        item={item}
                        syncKey={`${item._rowKey ?? index}:${item._posologyRev || 0}`}
                        loading={Boolean(posologyLoading[index])}
                        verified={posologyHints[index]?.source === 'verified_bula'}
                        onChange={(fields) => updatePosologyLine(index, fields)}
                      />
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flexWrap: 'wrap' }}>
                        <Box sx={{ flex: '1 1 260px', minWidth: 0 }}>
                          <PrescriptionMedicationDetailsFields
                            item={item}
                            formOptions={formOptions}
                            showActiveIngredient
                            onFieldChange={(field, value) => updateItem(index, field, value)}
                          />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', pt: 0.25 }}>
                          {ITEM_FLAGS.map((flag) => (
                            <ItemFlagChip
                              key={flag.field}
                              label={flag.label}
                              color={flag.color}
                              bg={flag.bg}
                              active={Boolean(item[flag.field])}
                              onToggle={() => updateItem(index, flag.field, !item[flag.field])}
                            />
                          ))}
                          {item.sncr_receita_tipo ? (
                            <Chip
                              size="small"
                              label={`SNCR ${item.sncr_receita_tipo}`}
                              sx={{ height: 22, fontSize: 10, fontWeight: 700, bgcolor: '#EEF2FF', color: '#3730A3' }}
                            />
                          ) : null}
                          <PrescriptionPackageCountChip
                            count={resolvePrescriptionPackageCount(item)}
                            onChange={(next) => {
                              setItems((current) => {
                                const updated = [...current];
                                updated[index] = {
                                  ...updated[index],
                                  ...prescriptionItemPackageFields(next),
                                };
                                return updated;
                              });
                            }}
                          />
                        </Box>
                      </Box>
                    </Box>
                    {items.length > 1 && (
                      <Tooltip title="Remover medicamento">
                        <IconButton
                          size="small"
                          onClick={() => removeItem(index)}
                          aria-label={`Remover medicamento ${index + 1}`}
                          sx={{ alignSelf: 'flex-start', color: '#94A3B8', '&:hover': { color: '#DC2626', bgcolor: '#FEF2F2' } }}
                        >
                          <RemoveIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          <Box>
            <Typography sx={{ ...sectionLabelSx, mb: 1.25 }}>Informações clínicas</Typography>
            <Grid container spacing={1.5}>
              {existingPrescriptionId && (
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    label="Data de emissão"
                    value={prescriptionForm.issue_date || ''}
                    onChange={(e) => {
                      const v = e.target.value;
                      setPrescriptionForm({ ...prescriptionForm, issue_date: v });
                      setScheduledIssueDates((prev) => {
                        const rest = prev.length > 1 ? prev.slice(1) : [];
                        return normalizeScheduledIssueDates([v, ...rest]);
                      });
                    }}
                    InputLabelProps={{ shrink: true, ...mobileFormFieldLabelProps(fullScreen) }}
                  />
                </Grid>
              )}
              <Grid item xs={12} sm={8}>
                <TextField fullWidth size="small" label="Diagnóstico" value={prescriptionForm.diagnosis}
                  onChange={(e) => setPrescriptionForm({ ...prescriptionForm, diagnosis: e.target.value })}
                  InputLabelProps={mobileFormFieldLabelProps(fullScreen)} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth size="small" label="CID-10" value={prescriptionForm.diagnosis_code}
                  onChange={(e) => setPrescriptionForm({ ...prescriptionForm, diagnosis_code: e.target.value })}
                  InputLabelProps={mobileFormFieldLabelProps(fullScreen)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Indicação clínica" value={prescriptionForm.clinical_indication} multiline minRows={2}
                  onChange={(e) => setPrescriptionForm({ ...prescriptionForm, clinical_indication: e.target.value })}
                  InputLabelProps={mobileFormFieldLabelProps(fullScreen)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Observações (impressas)" value={prescriptionForm.notes} multiline minRows={2}
                  onChange={(e) => setPrescriptionForm({ ...prescriptionForm, notes: e.target.value })}
                  InputLabelProps={mobileFormFieldLabelProps(fullScreen)} />
              </Grid>
            </Grid>
          </Box>
        </Box>

        <Box
          sx={{
            width: { xs: '100%', md: 360 },
            flexShrink: 0,
            overflowY: { md: 'auto' },
            bgcolor: '#F8FAFC',
            borderLeft: { md: '1px solid #E2E8F0' },
            borderTop: { xs: '1px solid #E2E8F0', md: 0 },
            px: 2.5,
            py: 2.5,
            display: 'flex',
            flexDirection: 'column',
            gap: 2.5,
          }}
        >
          {showReuse && (
            <Box sx={{ '& > *:last-child': { mb: 0 } }}>
              <Typography sx={{ ...sectionLabelSx, mb: 1.25 }}>Reutilizar</Typography>
              {templates.length > 0 && (
                <PrescriptionTemplateChipPicker
                  templates={templates}
                  onSelect={applyTemplate}
                  onReorder={handleReorderTemplates}
                  title="Modelos"
                  footerExtra={onManageTemplates ? (
                    <Button
                      size="small"
                      onClick={onManageTemplates}
                      sx={{ textTransform: 'none', color: '#0D9488', fontWeight: 600, p: 0, minWidth: 0 }}
                    >
                      Gerenciar modelos
                    </Button>
                  ) : undefined}
                />
              )}
              {activePatientId && (
                <PatientPrescriptionHistoryPicker
                  prescriptions={patientPrescriptionHistory}
                  loading={loadingPatientPrescriptionHistory}
                  onSelect={applyPatientPrescription}
                  title="Receitas anteriores do paciente"
                />
              )}
            </Box>
          )}

          {showRenewalAssist && (
            <Box>
              <Typography sx={{ ...sectionLabelSx, mb: 1.25 }}>Renovação</Typography>
              <PrescriptionRenewalAssist
                firstItemQuantity={items.find((i) => i.medication_name?.trim())?.quantity}
                firstItemPackageCount={resolvePrescriptionPackageCount(
                  items.find((i) => i.medication_name?.trim()) || {},
                )}
                returnMonths={renewalReturnMonths}
                onReturnMonthsChange={setRenewalReturnMonths}
                tabletsPerMonth={renewalTabletsPerMonth}
                onTabletsPerMonthChange={setRenewalTabletsPerMonth}
                currentMonthCount={multiMonthCount}
                currentMonthStep={multiMonthStep}
                onApplySuggestedMonths={(months, step) => {
                  setPrintSettings(buildMultiMonthPrintSettings(printSettings, months, step));
                }}
              />
            </Box>
          )}

          {showPrintMonthSettings && (
            <Box>
              <Typography sx={{ ...sectionLabelSx, mb: 1.25 }}>Impressão</Typography>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Meses na impressão"
                value={multiMonthCount}
                onChange={(e) => {
                  const raw = Number(e.target.value);
                  const next = Number.isFinite(raw) ? Math.min(12, Math.max(1, Math.floor(raw))) : 1;
                  setPrintSettings(buildMultiMonthPrintSettings(printSettings, next));
                }}
                inputProps={{ min: 1, max: 12, step: 1 }}
                helperText={multiMonthCount > 1 && multiMonthStep > 1
                  ? `Uma folha a cada ${multiMonthStep} meses na mesma receita.`
                  : 'Páginas mensais na mesma receita.'}
                sx={{ bgcolor: '#FFFFFF' }}
                InputLabelProps={mobileFormFieldLabelProps(fullScreen)}
              />
            </Box>
          )}

          <Box>
            <Typography sx={{ ...sectionLabelSx, mb: 1.25 }}>Notas internas</Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="Visível só para a equipe"
              value={prescriptionForm.internal_notes}
              multiline
              minRows={3}
              onChange={(e) => setPrescriptionForm({ ...prescriptionForm, internal_notes: e.target.value })}
              sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#FFFBEB' } }}
            />
            <Typography sx={{ fontSize: '0.72rem', color: '#94A3B8', mt: 0.5 }}>
              Não aparecem na impressão.
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: { xs: 2, md: 3 },
          py: 1.5,
          pb: fullScreen ? 'calc(12px + env(safe-area-inset-bottom, 0px))' : 1.5,
          borderTop: '1px solid #E2E8F0',
          gap: 1,
          bgcolor: '#FFFFFF',
          flexShrink: 0,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.25 }}>
          <Typography sx={{ fontSize: '0.78rem', color: '#64748B', display: { xs: 'none', sm: 'block' } }}>
            {effectiveFormat === 'structured'
              ? `${medicationCount} ${medicationCount === 1 ? 'medicamento' : 'medicamentos'} · `
              : ''}
            {prescriptionsToCreate} {prescriptionsToCreate === 1 ? 'receita' : 'receitas'}
            {multiMonthCount > 1 && effectiveFormat === 'structured'
              ? ` · ${multiMonthCount} folhas por receita${multiMonthStep > 1 ? ` (a cada ${multiMonthStep} meses)` : ''}`
              : ''}
            {hideIssueDateOnPrint ? ' · sem data na impressão' : ''}
          </Typography>
          {selectedPatient?.id && (
            <MemedPrescriptionButton
              patientId={selectedPatient.id}
              professionalId={selectedProfessional?.id || professionalId || null}
              onSaved={() => onMemedSaved?.()}
            />
          )}
        </Box>
        <Button onClick={resetAndClose} disabled={saving} sx={{ color: '#64748B', textTransform: 'none', fontWeight: 600 }}>
          Cancelar
        </Button>
        <Button
          variant="outlined"
          disableElevation
          onClick={() => void handleSave('save')}
          disabled={!canSave}
          sx={{
            textTransform: 'none', fontWeight: 600, borderRadius: 2, borderColor: '#CBD5E1', color: '#334155',
          }}
        >
          {savingAction === 'save' ? <CircularProgress size={20} /> : saveOnlyLabel}
        </Button>
        <Button
          variant="outlined"
          disableElevation
          startIcon={savingAction === 'print' ? undefined : <PrintIcon fontSize="small" />}
          onClick={() => void handleSave('print')}
          disabled={!canSave}
          sx={{
            textTransform: 'none', fontWeight: 600, borderRadius: 2, borderColor: '#CBD5E1', color: '#334155',
          }}
        >
          {savingAction === 'print' ? <CircularProgress size={20} /> : 'Salvar e imprimir'}
        </Button>
        {printHasMultipleVias && (
          <Tooltip title="Imprime somente a 1ª via de cada bloco (como na pré-visualização)">
            <span>
              <Button
                variant="outlined"
                disableElevation
                onClick={() => void handleSave('print_first_via')}
                disabled={!canSave}
                sx={{
                  textTransform: 'none', fontWeight: 600, borderRadius: 2, borderColor: '#CBD5E1', color: '#334155',
                  whiteSpace: 'nowrap',
                }}
              >
                {savingAction === 'print_first_via' ? <CircularProgress size={20} /> : 'Salvar · 1 via'}
              </Button>
            </span>
          </Tooltip>
        )}
        <Button
          variant="contained"
          disableElevation
          startIcon={savingAction === 'sign' ? undefined : <SignIcon fontSize="small" />}
          onClick={() => void handleSave('sign')}
          disabled={!canSave}
          sx={{
            bgcolor: '#2563EB', '&:hover': { bgcolor: '#1D4ED8' },
            textTransform: 'none', fontWeight: 600, borderRadius: 2,
          }}
        >
          {savingAction === 'sign' ? <CircularProgress size={20} sx={{ color: '#FFFFFF' }} /> : 'Salvar e assinar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PrescriptionFormDialog;
